import useFormContext from '@src/corelib/forms/generic/useFormContext';
import {
  getAbsencesIdsForRange,
  getUserAbsences,
} from '@src/pages/Absence/AbsenceTimeline/utils';
import { formatIsoDate } from '@src/utils';
import { useEffect } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import useAbsencesTimeline from './../../../../hooks';
import type { DatesChangedPayload } from './Dates';
import { DatesRangeField } from './Dates';
import NotesField from './Notes';
import ReasonField from './Reason';
import TentativeField from './Tentative';
import TimeRangeField from './Time';

type EventFormValues = {
  dateFrom?: Date;
  dateTo?: Date;
  startTime?: string | null;
  endTime?: string | null;
  typeId?: number | null;
  tentative?: boolean;
  notes?: string;
};

type EventFormFieldsetProps = {
  hoursView: boolean;
};

export default function EventFormFieldset({
  hoursView,
}: EventFormFieldsetProps) {
  const { setValue, watch } =
    useFormContext() as UseFormReturn<EventFormValues>;
  const { absences, setSelectedSlot, selectedSlotData } = useAbsencesTimeline();
  const [startTime, endTime] = watch(['startTime', 'endTime']);

  useEffect(() => {
    setSelectedSlot((slot) => {
      if (!slot) return null;
      return {
        ...slot,
        startTime: startTime || null,
        endTime: endTime || null,
      };
    });
  }, [startTime, endTime, setSelectedSlot]);

  const onDatesChanged = (dates: DatesChangedPayload) => {
    setSelectedSlot((slot) => {
      if (!slot) return null;
      const changedDates = {
        ...(dates.dateFrom ? { start: formatIsoDate(dates.dateFrom) } : {}),
        ...(dates.dateTo ? { end: formatIsoDate(dates.dateTo) } : {}),
      };
      return {
        ...slot,
        ...changedDates,
        selectedIds: getAbsencesIdsForRange(
          dates.dateFrom || new Date(slot.start),
          dates.dateTo || new Date(slot.end),
          getUserAbsences(slot.userId, absences),
        ),
      };
    });
  };

  useEffect(() => {
    if (selectedSlotData?.start) {
      setValue('dateFrom', new Date(selectedSlotData.start));
    }
    if (selectedSlotData?.end) {
      setValue('dateTo', new Date(selectedSlotData.end));
    }
    setValue('startTime', selectedSlotData?.startTime || null);
    setValue('endTime', selectedSlotData?.endTime || null);
    setValue('typeId', selectedSlotData?.typeId);
    setValue('tentative', selectedSlotData?.tentative);
    setValue('notes', selectedSlotData?.notes);
  }, [
    setValue,
    selectedSlotData?.start,
    selectedSlotData?.end,
    selectedSlotData?.startTime,
    selectedSlotData?.endTime,
    selectedSlotData?.notes,
    selectedSlotData?.tentative,
    selectedSlotData?.typeId,
  ]);

  return (
    <>
      <DatesRangeField hoursView={hoursView} onDatesChanged={onDatesChanged} />
      {hoursView && <TimeRangeField />}
      <ReasonField />
      <TentativeField />
      <NotesField />
    </>
  );
}
